import { MapUtils } from '../../../utility/map-utils.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/dto.js';
import { VersionRange } from '../../core/version-range.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { InvoiceProvider } from './invoice-provider.js';

export class LegacyInvoiceDetails extends DataModel {
  static readonly TYPE_ID: string = '5b1cc85d2b434e40935cf86659364b5e';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly provider: InvoiceProvider,
    public readonly uniqueId: string,
    public readonly name: string,
    public readonly downloadUrl: string | null,
  ) {
    super();
  }

  get $typeId() {
    return LegacyInvoiceDetails.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, LegacyInvoiceDetails.SCHEMA_VERSION);
  }

  static create(options: {
    provider: InvoiceProvider,
    uniqueId: string,
    name: string,
    downloadUrl: string | null,
  }): LegacyInvoiceDetails {
    return new LegacyInvoiceDetails(
        options.provider,
        options.uniqueId,
        options.name,
        options.downloadUrl,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyInvoiceDetails {
    return new LegacyInvoiceDetails(
        toEnumFromString(MapUtils.get<string>(map, 'provider'), InvoiceProvider),
        MapUtils.getString(map, 'uniqueId'),
        MapUtils.getString(map, 'name'),
        MapUtils.getStringOrNull(map, 'downloadUrl'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'provider': fromEnumAsString(this.provider),
      'uniqueId': this.uniqueId,
      'name': this.name,
      'downloadUrl': this.downloadUrl,
    };
  }

  clone(): this {
    return new LegacyInvoiceDetails(
        this.provider,
        this.uniqueId,
        this.name,
        this.downloadUrl,
    ) as this;
  }

  isSame(other: LegacyInvoiceDetails): boolean {
    if(this === other) {
      return true;
    }

    return this.provider === other.provider &&
      this.uniqueId === other.uniqueId &&
      this.name === other.name &&
      this.downloadUrl === other.downloadUrl;
  }
}

export class LegacyInvoiceDetailsBuilder extends DataModelBuilder<LegacyInvoiceDetails> {
  build(context: SerializationContext, map: SerializedData): LegacyInvoiceDetails {
    return LegacyInvoiceDetails.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(LegacyInvoiceDetails.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(LegacyInvoiceDetails.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the [name] field was added.
    if(version < 2) {
      map['name'] = map['uniqueId'];
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
